import graphqlClient from './graphqlClient';

export interface StoreLocation {
  latitude: number | null;
  longitude: number | null;
  site?: string; // Store name/site
  brand_name?: string;
  address?: string;
  purchek_install_date?: string | null;
  containment_install_date?: string | null;
  cart_manager_install_date?: string | null;
  subscription_start_date?: string | null;
  paired_wheel_install_date?: string | null;
  customer_id?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  county?: string | null;
  crime_rate?: number | null; // total_crime_incidents
  penetration?: number | null; // nearby_penetration percentage
  penetration_purchek?: number | null;
  penetration_containment?: number | null;
  penetration_paired_wheel?: number | null;
  is_customer?: boolean; // computed: customer_id exists OR any install date exists
  // Additional fields for recommendation calculations
  purchek_customer_count?: number | null;
  containment_customer_count?: number | null;
  no_of_hot_days?: number | null;
  no_of_cold_days?: number | null;
  penalty_for_cart_owner?: boolean | null;
  law_summary?: string | null;
  plaza_cm_customer_count?: number | null;
  estimated_no_of_carts_per_store?: number | null;
  channel?: string | null;
  shoplifting_amount_usd?: number | null;
  shoplifting_incident_count?: number | null;
}

export interface NearbyStore extends StoreLocation {
  id?: string;
  site?: string;
  address?: string;
  distance?: number; // Distance in miles
  containment_install_date?: string | null;
  cart_manager_install_date?: string | null;
  subscription_start_date?: string | null;
  paired_wheel_install_date?: string | null;
  total_crime_amount_usd?: number | null;
  video_incident_count?: number | null;
  store_notes?: Array<{
    id: string;
    comment: string;
    created_at?: string;
    user?: { id: string; name: string | null };
  }>;
  updated_store_ids?: Array<{ store_id: string; created_at?: string }> | null;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

/**
 * Fetch store details by POI ID
 * Used for the store map page to display individual store information
 */
export const fetchStoreDetails = async (poiId: string): Promise<StoreLocation | null> => {
  try {
    const query = `
      query GetStoreDetails($poiId: String!) @cached {
        udm_poi(where: { id: { _eq: $poiId } }) {
          latitude
          longitude
          site
          brand_name
          address
          channel
          customer_id
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          geo {
            total_crime_incidents
            city
            state
            zipcode
            county
            no_of_hot_days
            no_of_cold_days
            penalty_for_cart_owner
            law_summary
          }
          nearby_penetration {
            penetration
            penetration_purchek
            penetration_containment
            penetration_paired_wheel
            purchek_customer_count
            containment_customer_count
            plaza_cm_customer_count
            shoplifting_amount_usd
            shoplifting_incident_count
          }
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, { poiId });
    const poi = response.udm_poi?.[0];

    if (poi) {
      // Determine if customer: has customer_id OR has any install date
      const hasCustomerId = !!poi.customer_id;
      const hasAnyInstall = !!(
        poi.purchek_install_date ||
        poi.containment_install_date ||
        poi.cart_manager_install_date ||
        poi.subscription_start_date ||
        poi.paired_wheel_install_date
      );
      const is_customer = hasCustomerId || hasAnyInstall;

      // Fetch estimated_no_of_carts_per_store from brand table
      let estimatedCarts: number | null = null;
      if (poi.brand_name) {
        try {
          const brandQuery = `
            query GetBrandCarts($brandName: String!) @cached {
              udm_brand(where: { name: { _eq: $brandName } }) {
                estimated_no_of_carts_per_store
              }
            }
          `;
          const brandResponse = await graphqlClient.request<any>(brandQuery, { brandName: poi.brand_name });
          estimatedCarts = brandResponse.udm_brand?.[0]?.estimated_no_of_carts_per_store ?? null;
        } catch (e) {
          console.error('Failed to fetch brand estimated carts', e);
        }
      }

      return {
        latitude: poi.latitude || null,
        longitude: poi.longitude || null,
        site: poi.site || undefined,
        brand_name: poi.brand_name || undefined,
        address: poi.address || undefined,
        channel: poi.channel || null,
        customer_id: poi.customer_id || null,
        purchek_install_date: poi.purchek_install_date || null,
        containment_install_date: poi.containment_install_date || null,
        cart_manager_install_date: poi.cart_manager_install_date || null,
        subscription_start_date: poi.subscription_start_date || null,
        paired_wheel_install_date: poi.paired_wheel_install_date || null,
        city: poi.geo?.city || null,
        state: poi.geo?.state || null,
        zipcode: poi.geo?.zipcode || null,
        county: poi.geo?.county || null,
        crime_rate: poi.geo?.total_crime_incidents || null,
        penetration: poi.nearby_penetration?.penetration || null,
        penetration_purchek: poi.nearby_penetration?.penetration_purchek || null,
        penetration_containment: poi.nearby_penetration?.penetration_containment || null,
        penetration_paired_wheel: poi.nearby_penetration?.penetration_paired_wheel || null,
        is_customer,
        purchek_customer_count: poi.nearby_penetration?.purchek_customer_count ?? null,
        containment_customer_count: poi.nearby_penetration?.containment_customer_count ?? null,
        no_of_hot_days: poi.geo?.no_of_hot_days ?? null,
        no_of_cold_days: poi.geo?.no_of_cold_days ?? null,
        penalty_for_cart_owner: poi.geo?.penalty_for_cart_owner ?? null,
        law_summary: poi.geo?.law_summary ?? null,
        plaza_cm_customer_count: poi.nearby_penetration?.plaza_cm_customer_count ?? null,
        estimated_no_of_carts_per_store: estimatedCarts,
        shoplifting_amount_usd: poi.nearby_penetration?.shoplifting_amount_usd ?? null,
        shoplifting_incident_count: poi.nearby_penetration?.shoplifting_incident_count ?? null
      };
    }

    return null;
  } catch (error) {
    console.error('fetchStoreDetails error', error);
    return null;
  }
};

/**
 * Fetch multiple stores by their POI IDs in a single query
 * Returns array of StoreLocation objects
 */
export const fetchStoresByIds = async (poiIds: string[]): Promise<(StoreLocation & { id: string })[]> => {
  if (!poiIds || poiIds.length === 0) return [];
  try {
    const query = `
      query GetStoresByIds($poiIds: [String!]!) @cached {
        udm_poi(where: { id: { _in: $poiIds }, closed_on: { _is_null: true } }) {
          id
          latitude
          longitude
          site
          brand_name
          address
          channel
          customer_id
          lifetime_value
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          rank
          cart_manager_rank
          purchek_rank
          containment_rank
          geo {
            total_crime_incidents
            city
            state
            zipcode
            county
            crime_rate
            no_of_hot_days
            no_of_cold_days
            penalty_for_cart_owner
            law_summary
            law_source
          }
          nearby_penetration {
            penetration
            penetration_purchek
            penetration_containment
            penetration_paired_wheel
            purchek_customer_count
            containment_customer_count
            plaza_cm_customer_count
            shoplifting_amount_usd
            shoplifting_incident_count
          }
          wheel {
            single_wheel_replacement_rate
            service_frequency
          }
          video {
            total_crime_amount_usd
            incident_count
          }
          store_feedbacks(order_by: {created_at: desc}) {
            id
            comment
            created_at
            updated_at
            user {
              id
              name
            }
          }
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, { poiIds });
    const pois = response.udm_poi || [];

    // Fetch estimated carts per store for each unique brand
    const brandNames = Array.from(new Set(pois.map((p: any) => p.brand_name).filter(Boolean))) as string[];
    let brandCartsMap: Record<string, number> = {};
    if (brandNames.length > 0) {
      try {
        const brandQuery = `
          query GetBrandsCarts($brandNames: [String!]!) @cached {
            udm_brand(where: { name: { _in: $brandNames } }) {
              name
              estimated_no_of_carts_per_store
            }
          }
        `;
        const brandResponse = await graphqlClient.request<any>(brandQuery, { brandNames });
        (brandResponse.udm_brand || []).forEach((b: any) => {
          brandCartsMap[b.name] = b.estimated_no_of_carts_per_store || 0;
        });
      } catch (e) {
        console.error('Failed to fetch brand estimated carts', e);
      }
    }

    return pois.map((poi: any) => ({
      id: poi.id,
      latitude: poi.latitude || null,
      longitude: poi.longitude || null,
      site: poi.site || undefined,
      brand_name: poi.brand_name || undefined,
      address: poi.address || undefined,
      channel: poi.channel || null,
      customer_id: poi.customer_id || null,
      purchek_install_date: poi.purchek_install_date || null,
      containment_install_date: poi.containment_install_date || null,
      cart_manager_install_date: poi.cart_manager_install_date || null,
      subscription_start_date: poi.subscription_start_date || null,
      paired_wheel_install_date: poi.paired_wheel_install_date || null,
      city: poi.geo?.city || null,
      state: poi.geo?.state || null,
      zipcode: poi.geo?.zipcode || null,
      county: poi.geo?.county || null,
      crime_rate: poi.geo?.total_crime_incidents || null,
      penetration: poi.nearby_penetration?.penetration || null,
      penetration_purchek: poi.nearby_penetration?.penetration_purchek || null,
      penetration_containment: poi.nearby_penetration?.penetration_containment || null,
      penetration_paired_wheel: poi.nearby_penetration?.penetration_paired_wheel || null,
      is_customer: !!(poi.customer_id || poi.purchek_install_date || poi.containment_install_date || poi.cart_manager_install_date || poi.subscription_start_date || poi.paired_wheel_install_date),
      purchek_customer_count: poi.nearby_penetration?.purchek_customer_count ?? null,
      containment_customer_count: poi.nearby_penetration?.containment_customer_count ?? null,
      no_of_hot_days: poi.geo?.no_of_hot_days ?? null,
      no_of_cold_days: poi.geo?.no_of_cold_days ?? null,
      penalty_for_cart_owner: poi.geo?.penalty_for_cart_owner ?? null,
      law_summary: poi.geo?.law_summary ?? null,
      plaza_cm_customer_count: poi.nearby_penetration?.plaza_cm_customer_count ?? null,
      estimated_no_of_carts_per_store: brandCartsMap[poi.brand_name] ?? null,
      shoplifting_amount_usd: poi.nearby_penetration?.shoplifting_amount_usd ?? null,
      shoplifting_incident_count: poi.nearby_penetration?.shoplifting_incident_count ?? null,
      // Extra fields for AllSites mapping
      lifetime_value: poi.lifetime_value ?? null,
      rank: poi.rank ?? null,
      cart_manager_rank: poi.cart_manager_rank ?? null,
      purchek_rank: poi.purchek_rank ?? null,
      containment_rank: poi.containment_rank ?? null,
      geo: poi.geo || null,
      nearby_penetration: poi.nearby_penetration || null,
      wheel: poi.wheel || null,
      video: poi.video || null,
      store_feedback: (poi.store_feedbacks || []).map((f: any) => ({
        id: f.id,
        comment: f.comment,
        created_at: f.created_at,
        updated_at: f.updated_at,
        user: f.user,
      })),
    }));
  } catch (error) {
    console.error('fetchStoresByIds error', error);
    return [];
  }
};

/**
 * Calculate bounding box coordinates for a given radius around a center point
 * Returns min/max latitude and longitude for filtering
 * Using approximately 0.0145 degrees per mile (at equator)
 */
const calculateBoundingBox = (
  centerLat: number,
  centerLng: number,
  radiusMiles: number
): { minLat: number; maxLat: number; minLng: number; maxLng: number } => {
  const degreesPerMile = 0.0145;
  const latOffset = radiusMiles * degreesPerMile;
  const lngOffset = (radiusMiles * degreesPerMile) / Math.cos((centerLat * Math.PI) / 180);

  return {
    minLat: centerLat - latOffset,
    maxLat: centerLat + latOffset,
    minLng: centerLng - lngOffset,
    maxLng: centerLng + lngOffset
  };
};

/**
 * Fetch all nearby stores within a bounding box radius
 * Returns stores within specified radius (default 5 miles) from a reference point
 */
export const fetchNearbyStores = async (
  referenceLat: number,
  referenceLng: number,
  radiusMiles: number = 5,
  referenceStoreId?: string
): Promise<NearbyStore[]> => {
  try {
    const bbox = calculateBoundingBox(referenceLat, referenceLng, radiusMiles);

    console.log('Bounding box calculated:', bbox);

    const query = `
      query GetNearbyStores(
        $minLat: numeric!
        $maxLat: numeric!
        $minLng: numeric!
        $maxLng: numeric!
      ) @cached {
        udm_poi(
          where: {
            _and: [
              { latitude: { _gte: $minLat } }
              { latitude: { _lte: $maxLat } }
              { longitude: { _gte: $minLng } }
              { longitude: { _lte: $maxLng } }
            ]
          }
        ) {
          id
          site
          address
          latitude
          longitude
          brand_name
          channel
          customer_id
          purchek_install_date
          containment_install_date
          cart_manager_install_date
          subscription_start_date
          paired_wheel_install_date
          geo {
            total_crime_incidents
            city
            state
            zipcode
            county
            no_of_hot_days
            no_of_cold_days
            penalty_for_cart_owner
            law_summary
          }
          nearby_penetration {
            penetration
            penetration_purchek
            penetration_containment
            penetration_paired_wheel
            purchek_customer_count
            containment_customer_count
            plaza_cm_customer_count
            shoplifting_amount_usd
            shoplifting_incident_count
          }
          video {
            total_crime_amount_usd
            incident_count
          }
          store_notes(order_by: {created_at: desc}) {
            id
            comment
            created_at
            user {
              id
              name
            }
          }
          updated_store_ids(order_by: {created_at: desc}, limit: 1) {
            store_id
            created_at
          }
        }
      }
    `;

    const response = await graphqlClient.request<any>(query, {
      minLat: bbox.minLat,
      maxLat: bbox.maxLat,
      minLng: bbox.minLng,
      maxLng: bbox.maxLng
    });

    const stores = response.udm_poi || [];

    const nearbyStores: NearbyStore[] = stores
      .filter((store: any) => store.latitude && store.longitude)
      .filter((store: any) => !referenceStoreId || store.id !== referenceStoreId)
      .map((store: any) => {
        const hasCustomerId = !!store.customer_id;
        const hasAnyInstall = !!(
          store.purchek_install_date ||
          store.containment_install_date ||
          store.cart_manager_install_date ||
          store.subscription_start_date ||
          store.paired_wheel_install_date
        );
        return {
        id: store.id,
        site: store.site,
        address: store.address,
        latitude: store.latitude,
        longitude: store.longitude,
        brand_name: store.brand_name,
        channel: store.channel || null,
        customer_id: store.customer_id || null,
        purchek_install_date: store.purchek_install_date,
        containment_install_date: store.containment_install_date,
        cart_manager_install_date: store.cart_manager_install_date,
        subscription_start_date: store.subscription_start_date,
        paired_wheel_install_date: store.paired_wheel_install_date,
        city: store.geo?.city || null,
        state: store.geo?.state || null,
        zipcode: store.geo?.zipcode || null,
        county: store.geo?.county || null,
        crime_rate: store.geo?.total_crime_incidents || null,
        penetration: store.nearby_penetration?.penetration || null,
        penetration_purchek: store.nearby_penetration?.penetration_purchek || null,
        penetration_containment: store.nearby_penetration?.penetration_containment || null,
        penetration_paired_wheel: store.nearby_penetration?.penetration_paired_wheel || null,
        is_customer: hasCustomerId || hasAnyInstall,
        purchek_customer_count: store.nearby_penetration?.purchek_customer_count ?? null,
        containment_customer_count: store.nearby_penetration?.containment_customer_count ?? null,
        no_of_hot_days: store.geo?.no_of_hot_days ?? null,
        no_of_cold_days: store.geo?.no_of_cold_days ?? null,
        penalty_for_cart_owner: store.geo?.penalty_for_cart_owner ?? null,
        law_summary: store.geo?.law_summary ?? null,
        plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
        shoplifting_amount_usd: store.nearby_penetration?.shoplifting_amount_usd ?? null,
        shoplifting_incident_count: store.nearby_penetration?.shoplifting_incident_count ?? null,
        total_crime_amount_usd: store.video?.total_crime_amount_usd ?? null,
        video_incident_count: store.video?.incident_count ?? null,
        store_notes: store.store_notes || [],
        distance: calculateDistance(
          referenceLat,
          referenceLng,
          store.latitude,
          store.longitude
        )
      };
      })
      .filter((store: NearbyStore) => store.distance! < radiusMiles)
      .sort((a: NearbyStore, b: NearbyStore) => (a.distance || 0) - (b.distance || 0));

    console.log('Nearby stores found:', nearbyStores);
    return nearbyStores;
  } catch (error) {
    console.error('fetchNearbyStores error', error);
    return [];
  }
};

export default fetchStoreDetails;
